import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { LanguageCode } from '../../Models/Models';
import { useLanguageCatalog } from '../../Hooks/useLanguageCatalog';
import { languageName, nativeLanguageName } from '../../Services/languageNames';

// Flags come from flag-icons (4:3 SVGs). Languages without a home flag (Esperanto, Volapük, ...)
// show their code instead.
export const FlagImage: React.FC<{ code: LanguageCode; size?: 'sm' | 'md' }> = ({ code, size = 'md' }) => {
    const catalog = useLanguageCatalog();
    const flagCountry = catalog?.get(code)?.flagCountry;

    return (
        <span className={`st-flag st-flag--${size}`} title={languageName(code)}>
            {flagCountry
                ? <span className={`fi fi-${flagCountry} st-flag__img`} />
                : <span className="st-flag__code">{code}</span>}
        </span>
    );
};

export const AddLanguageTile: React.FC<{ accentColor: string; onClick: () => void }> = ({ accentColor, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="st-lang-add"
        style={{ '--lang-accent': accentColor } as React.CSSProperties}
    >
        <FontAwesomeIcon icon={faPlus} />
        Pridať jazyk
    </button>
);

export interface LanguagePickerModalProps {
    show: boolean;
    title: string;
    accentColor: string;
    options: readonly LanguageCode[];
    onHide: () => void;
    onPick: (code: LanguageCode) => void;
    /** Show a search box (for long lists such as the whole catalog). */
    searchable?: boolean;
    /** Shown when there is nothing to pick, e.g. a link to enable more languages. */
    emptyContent?: React.ReactNode;
}

export const LanguagePickerModal: React.FC<LanguagePickerModalProps> = ({
    show, title, accentColor, options, onHide, onPick, searchable = false, emptyContent,
}) => {
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (show) setQuery('');
    }, [show]);

    const searchIndex = useMemo(
        () => searchable
            ? options.map(code => ({
                code,
                haystack: `${code} ${languageName(code)} ${nativeLanguageName(code)}`.toLocaleLowerCase('sk'),
            }))
            : [],
        [options, searchable]
    );

    const needle = query.trim().toLocaleLowerCase('sk');
    const visible = searchable && needle
        ? searchIndex.filter(x => x.haystack.includes(needle)).map(x => x.code)
        : options;

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size={searchable ? 'lg' : undefined}
            className="st-lang-modal"
            style={{ '--lang-accent': accentColor } as React.CSSProperties}
        >
            <div className="st-lang-modal__bar" />
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {searchable && (
                    <div className="st-lang-search">
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="st-lang-search__icon" />
                        <input
                            type="search"
                            autoFocus
                            className="form-control st-lang-search__input"
                            placeholder="Hľadať jazyk (názov alebo kód)…"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                        />
                    </div>
                )}
                {visible.length === 0 ? (
                    <div className="st-lang-modal__empty">
                        {options.length === 0
                            ? (emptyContent ?? 'Žiadne ďalšie jazyky nie sú k dispozícii.')
                            : 'Žiadny jazyk nezodpovedá hľadaniu.'}
                    </div>
                ) : (
                    <div className={`st-lang-grid${searchable ? ' st-lang-grid--scroll' : ''}`}>
                        {visible.map(code => (
                            <LanguageTile key={code} code={code} accentColor={accentColor} onClick={() => onPick(code)} />
                        ))}
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export const LanguageTile: React.FC<{
    code: LanguageCode;
    accentColor: string;
    onClick: () => void;
}> = ({ code, accentColor, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="st-lang-tile"
        style={{ '--lang-accent': accentColor } as React.CSSProperties}
    >
        <FlagImage code={code} size="md" />
        <span className="st-lang-tile__label">{languageName(code)}</span>
    </button>
);

/** Empty-state hint for editors when every enabled language is already used. */
export const EnableLanguagesHint: React.FC = () => (
    <>
        Žiadne ďalšie povolené jazyky.{' '}
        <Link to="/language/table">Spravovať jazyky stránky</Link>
    </>
);
